'use client'

/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import dynamic from 'next/dynamic';
import { ComponentNames as WnCComponentNames } from 'lib/constant/component-constant';
import MainCanvasProps from './WnCMainCanvasProps';
import MainCanvasState from './WnCMainCanvasState';

/**---------------------------------------------------------------------------------
 * Core Components -- Load always ------------ */
const DtvFirstName = dynamic(() => import("@directv-digital/dds.ui.dtv-firstname"));
import { useStoryblokConfigs } from '@directv-digital/dds.server.application-context';
import DtvBox from '@directv-digital/dds.ui.dtv-box';
import DtvVerticalSpacer from '@directv-digital/dds.ui.dtv-vertical-spacer';
// dynamic (SSR stays on) so these leave the entry chunk: DtvHero isn't rendered
// on every page, and the others are below-the-fold on most CMS stories.
const DtvPageSectionHeader = dynamic(() => import('@directv-digital/dds.ui.dtv-page-section-header'));
const DtvInPageLegal = dynamic(() => import('@directv-digital/dds.ui.dtv-in-page-legal'));
//import ContentCards from '@directv-digital/dds.ui.dtv-content-cards';
const ContentCards = dynamic(() => import('@/video-lab/dtv-content-cards'));
const DtvHero = dynamic(() => import('@directv-digital/dds.ui.dtv-hero'));
// WAT-10279: MarketingAI modal now controlled by page-load-modal orchestrator
// import MarketingAiModal from "@/video-lab/marketing-ai-modal";
const ErrorDisplay = dynamic(() => import("@directv-digital/dds.ui.error-display"), {ssr: false});
// WAT-10279: Revisitor modal now controlled by page-load-modal orchestrator
// const DtvRevisitorModal = dynamic(() => import('@/video-lab/dtv-revisitor-modal/dtv-revisitor-modal').then(mod => mod.DtvRevisitorModal), {ssr: false});

/**---------------------------------------------------------------------------------
 * Channel/GP Tools (maintain as relative imports, publish to VL as needed) ------------- */
const ChnlListingPage = dynamic(
  () => import("src/components/ChnlListingPageV2"));
const DtvAgentPackageCompareTool = dynamic(
      () => import("src/components/dtv-agent-package-compare-tool"));
const ChlupInteractiveToolV2 = dynamic(
  () => import("src/components/ChlupInteractiveToolV2"));
const LocalRsnTool = dynamic(
  () => import("src/components/video-lab/local-rsn-tool"));
const RsnLookupTool = dynamic(
  () => import("src/components/video-lab/rsn-lookup-tool"));
const RsnFeeLookup = dynamic(
  () => import("src/components/video-lab/rsn-fee-lookup"));
const LocalChannelsLookupPage = dynamic(
  () => import("src/components/LocalChannelsLookupPage"));
const PackageCarousel = dynamic(
  () => import("@/video-lab/package-carousel/package-carousel"));
const FlipCards = dynamic(
  () => import("@/video-lab/flip-cards").then(mod => ({ default: mod.FlipCards })),
);

/**---------------------------------------------------------------------------------
 * Components (maintain as a video lab import, unless otherwise needed) ------------
 * Please try to keep me alphabetized :) ------------------------------------------- */
const AmbientVideoMarquee = dynamic(
  () => import("@/video-lab/ambient-video-marquee"));
const ChannelLogoCarousal = dynamic(
  () => import("@directv-digital/dds.ui.channel-logo-carousal"));
const DishPointer = dynamic( // Note: UF WnC Canvas only (No StreamTV)
  () => import("@directv-digital/dds.ui.dtv-dish-pointer"));
const Dtv4UpIconList = dynamic(
  () => import("@directv-digital/dds.ui.dtv-four-up-icon-list"));
const DtvAgnosticPlanCards = dynamic(
  () => import('@directv-digital/dds.ui.dtv-agnostic-plan-cards'));
const DtvAppDownloadBanner = dynamic(
  () => import('@/video-lab/dtv-app-download-banner'));
const DtvBreadCrumbs = dynamic(
  () => import("@directv-digital/dds.ui.dtv-breadcrumbs"));
const DtvCompetitorTableModule = dynamic(
  () => import("@/video-lab/competitor-table"));
const DtvCountdown = dynamic(
  () => import("@directv-digital/dds.ui.dtv-countdown"));
const DtvEmailCapture = dynamic(
  () => import("@directv-digital/dds.ui.dtv-email-capture"));
const DtvEmailModal = dynamic(
  () => import("@directv-digital/dds.ui.dtv-email-capture-popup-modal"));
const DtvExitModal = dynamic(
  () => import("src/components/video-lab/dtv-exit-modal"), {ssr: false});
const DtvExternalScriptLoader = dynamic(
  () => import("@directv-digital/dds.ui.dtv-external-script-loader"));
const DtvExternalServiceComparison = dynamic(
  () => import("@directv-digital/dds.ui.dtv-external-service-comparison-chart"));
const DtvExternalServiceComparisonChartTabs = dynamic(
  () => import("@directv-digital/dds.ui.dtv-external-service-comparison-chart-tabs"));
const DtvFaq = dynamic(
  () => import('@directv-digital/dds.ui.dtv-faq'));
const DtvFormBuilder = dynamic(
  () => import("@directv-digital/dds.ui.dtv-form-builder"));
const DtvFindRetailer = dynamic(
  () => import("@directv-digital/dds.ui.dtv-find-retailer"));
const DtvFloatingwidget = dynamic(
  () => import("@directv-digital/dds.ui.dtv-floating-widget-v3"), { ssr: false });
const DtvHeadLine = dynamic(
  () => import("@directv-digital/dds.ui.dtv-headline"));
const DtvInsider = dynamic(
  () => import("@directv-digital/dds.ui.dtv-insider"));
const DtvInternalProductComparisionChart = dynamic(
  () => import("@directv-digital/dds.ui.dtv-internal-product-comparison-chart"));
const DtvLinkForest = dynamic(
  () => import("@directv-digital/dds.ui.link-forest"));
const DtvListItem = dynamic(
  () => import("@directv-digital/dds.ui.dtv-list"));
const DtvLongFormatCard = dynamic(
  () => import('@directv-digital/dds.ui.dtv-longformat-card'));
const DtvMduHero = dynamic(
  () => import("@directv-digital/dds.ui.dtv-mdu-hero"));
const DtvOfferCards = dynamic(
  () => import("@/video-lab/dtv-offer-cards/dtv-offer-cards").then(mod => mod.DtvOfferCards));
const DtvQuizComponent = dynamic(
  () => import("@directv-digital/dds.ui.dtv-quiz-component"));
const DtvThreeUp = dynamic(
  () => import("@directv-digital/dds.ui.dtv-three-up"));
const EquipmentWizard = dynamic(
  () => import("@directv-digital/dds.ui.equipment-wizard"));
const featuredArticleList = dynamic(
  () => import("@directv-digital/dds.ui.dtv-featured-article-list"));
const ImageMarquee = dynamic(
  () => import("@directv-digital/dds.ui.image-marquee"));
const InPageNav = dynamic(
  () => import("@directv-digital/dds.ui.inpagenav"));
const MarqueeCarousel = dynamic(
  () => import("@directv-digital/dds.ui.marquee-carousel"));
const MediaModule = dynamic(
  () => import("@directv-digital/dds.ui.dtv-media-module"));
const MessagingBanner = dynamic(
  () => import("@directv-digital/dds.ui.dtv-messaging-banner"));
const MessagingCard = dynamic(
  () => import("@directv-digital/dds.ui.dtv-messaging-card"));
const PackageComparisonSavingsCalculator = dynamic(
  () => import("@directv-digital/dds.ui.package-comparison-calculator"));
const PlanPortal = dynamic(
  () => import('@directv-digital/dds.ui.plan-portal'));
const ReviewCarousel = dynamic(
  () => import("src/components/video-lab/review-carousel"));
const RotatingMarque = dynamic(
  () => import("@directv-digital/dds.ui.rotating-marque"));
const DtvRowCanvas = dynamic(
  () => import("@directv-digital/dds.ui.dtv-row-canvas"));
const ScrollToTop = dynamic(
  () => import("@directv-digital/dds.ui.scroll-to-top"));
const SearchComponent = dynamic(
  () => import("@directv-digital/dds.ui.search-component"));
const SearchWatchNow = dynamic(
  () => import("@directv-digital/dds.ui.search-guide"));
const SeoContent = dynamic(
  () => import("@directv-digital/dds.ui.seo-content"));
const SiteMap = dynamic(
  () =>import ('@directv-digital/dds.ui.dtv-sitemap'));
const ShowCarousal = dynamic(
  () => import("src/components/video-lab/show-carousal"));
const TvPromise = dynamic(
  () => import("@directv-digital/dds.ui.tv-promise"));
const UcgGalleryLoader = dynamic(
  () => import("@directv-digital/dds.ui.ucg-gallery-loader"));
const VerticalVideoCarousel = dynamic(
  () => import("@directv-digital/dds.ui.vertical-video-carousel"));
const VideoMarquee = dynamic(
  () => import("@directv-digital/dds.ui.video-marquee"));
const videoCarousel = dynamic(
  () => import("@directv-digital/dds.ui.video-carousel"));
const VideoMarqueeCampaign = dynamic(
  () => import('@directv-digital/dds.ui.video-marquee-campaign'));
const VideoPopUpMarquee = dynamic(
  () => import("@directv-digital/dds.ui.video-popup-marquee"));
const YotpoPackageReview = dynamic(
  () => import("@directv-digital/dds.ui.yotpo-package-review"));
const YotpoPackageRating = dynamic(
  () => import("@directv-digital/dds.ui.yotpo-package-rating"));
const DtvMarquee = dynamic(()=> import("@directv-digital/dds.ui.dtv-marquee"));

/**
 * Fusion components
 */
const ContentCardC = dynamic(
  () => import('@directv-digital/fusion.organisms.content-card-c'));

/**---------------------------------------------------------------------------------
 * Components (maintain as relative imports, publish to VL as needed) ------------- */
const ConspiracyBoard = dynamic(
  () => import("src/components/video-lab/conspiracy-board"));
const DtvPlanCardModule = dynamic(
  () => import('src/components/video-lab/plan-cards/dtv-plan-card-v2'));
const DtvHorizontalPlanCard = dynamic(
  () => import("src/components/video-lab/plan-cards/dtv-horizontal-plan-card"));
const DtvTogglePlanCard = dynamic(
  () => import("src/components/video-lab/plan-cards/dtv-toggle-plan-card"));
const Iframe = dynamic(
  () => import("src/components/iframe"));
const FlappyPigeon = dynamic(
  () => import('src/components/flappy-pigeon/components/flappy-pigeon'));
const PackageCompareTool = dynamic(
  () => import("@/video-lab/package-compare-tool"));
const ChannelPackLineup = dynamic(
  () => import("@/video-lab/channel-pack-lineup"));
const InPageChannelPackLineup = dynamic(
    () => import("@/video-lab/inpage-channel-pack-lineup"));
const GenrePreviewTool = dynamic(
  () => import("@/video-lab/genre-preview-tool"));
const MediaCardImage1Up2UpV2 = dynamic(
  () => import("@/video-lab/media-module-v2/media-module-image"));
const GenrePackDetailsPanel = dynamic(
    () => import("@/video-lab/genre-pack-details-panel/genre-pack-details-panel"));
  const DtvCompetitorComparisonTable = dynamic(
    () => import("@/video-lab/dtv-competitor-comparison-table"));
const MarketingBanner = dynamic(
  () => import("@directv-digital/dds.ui.marketing-banner"));
const ChannelCompareToolV3 = dynamic(
  () => import("@/video-lab/channel-compare-tool-v3"));
const CustomerAcqCompareTool = dynamic(
  () => import("src/components/custom-acq-compare-tool"));

const WnCComponentMap: { [key: string]: any } = {
  [WnCComponentNames.INPAGENAV]: InPageNav,
  [WnCComponentNames.DTVMARQUEE]:DtvMarquee,
  [WnCComponentNames.DTVCAROUSEL]: MarqueeCarousel,
  [WnCComponentNames.ROTATINGMARQUEE]: RotatingMarque,
  [WnCComponentNames.VIDEOMARQUEE]: VideoMarquee,
  [WnCComponentNames.IMAGEMARQUEE]: ImageMarquee,
  [WnCComponentNames.VIDEOPOPUPMARQUEE]: VideoPopUpMarquee,
  [WnCComponentNames.VIDEOCAROUSEL]: videoCarousel,
  [WnCComponentNames.DISHPOINTER]: DishPointer,
  [WnCComponentNames.LONGFORMATCARD]: DtvLongFormatCard,
  [WnCComponentNames.CONTENTCARDS]: ContentCards,
  [WnCComponentNames.PAGESECTIONHEADER]: DtvPageSectionHeader,
  [WnCComponentNames.PLANCARD]: DtvPlanCardModule,
  [WnCComponentNames.HORIZONTAL_PLAN_CARD]: DtvHorizontalPlanCard,
  [WnCComponentNames.TOGGLE_PLAN_CARD]: DtvTogglePlanCard,
  [WnCComponentNames.FAQ]: DtvFaq,
  [WnCComponentNames.INPAGELEGAL]: DtvInPageLegal,
  [WnCComponentNames.BREADCRUMBSV2]: DtvBreadCrumbs,
  [WnCComponentNames.INTERNALPRODCUTCOMPARISIONCHART]: DtvInternalProductComparisionChart,
  [WnCComponentNames.DTVHERO]: DtvHero,
  [WnCComponentNames.VERTICALSPACER]: DtvVerticalSpacer,
  [WnCComponentNames.HEADLINE]: DtvHeadLine,
  [WnCComponentNames.LIST_ITEM]: DtvListItem,
  [WnCComponentNames.FOUR_UP_ICON_LIST]: Dtv4UpIconList,
  [WnCComponentNames.MESSAGINGBANNER]: MessagingBanner,
  [WnCComponentNames.MESSAGINGCARD]: MessagingCard,
  [WnCComponentNames.EXTERNALSERVICECOMPARISON]: DtvExternalServiceComparison,
  [WnCComponentNames.EXTERNALSERVICECOMPARISONTABS]: DtvExternalServiceComparisonChartTabs,
  [WnCComponentNames.DTV1UP2UPMEDIA]: MediaModule,
  [WnCComponentNames.DTVAGNOSTICPLANCARDS]: DtvAgnosticPlanCards,
  [WnCComponentNames.DTVEXTERNALSCRIPTLOADER]: DtvExternalScriptLoader,
  [WnCComponentNames.DTVMDUHERO]: DtvMduHero,
  [WnCComponentNames.DTVYOTPOGALLERYWRAPPER]: UcgGalleryLoader,
  [WnCComponentNames.FLOATINGWIDGETV2]: DtvFloatingwidget,
  [WnCComponentNames.FORM_BUILDERV2]: DtvFormBuilder,
  [WnCComponentNames.QUIZV2]: DtvQuizComponent,
  [WnCComponentNames.EMAILPOPUPMODAL]: DtvEmailModal,
  [WnCComponentNames.EMAILCAPTUREV2]: DtvEmailCapture,
  [WnCComponentNames.COMPETITORTABLE] : DtvCompetitorTableModule,
  [WnCComponentNames.DTVIFRAME]: Iframe,
  [WnCComponentNames.ROWCANVASV2]: DtvRowCanvas,
  [WnCComponentNames.CHANNEL_COMPARISON_TOOL_V2]: ChannelCompareToolV3,
  [WnCComponentNames.CHANNEL_LISTING_PAGE] : ChnlListingPage,
  [WnCComponentNames.DTVAGENTPACKAGECOMPARETOOL] : DtvAgentPackageCompareTool,
  [WnCComponentNames.SATELLITE_CHANNEL_LISTING_PAGE] : ChnlListingPage,
  [WnCComponentNames.INSIDERARTICLE]: DtvInsider,
  [WnCComponentNames.SITEMAP] : SiteMap,
  [WnCComponentNames.CHANNELLOGOCAROUSAL] : ChannelLogoCarousal,
  [WnCComponentNames.DTVFIRSTNAME] : DtvFirstName,
  [WnCComponentNames.DTVTHREEUP] : DtvThreeUp,
  [WnCComponentNames.LINKFOREST] : DtvLinkForest,
  [WnCComponentNames.CHLUP_INTERACTIVE_TOOL] : ChlupInteractiveToolV2,
  [WnCComponentNames.PACKAGECOMPARISONCALCULATOR]: PackageComparisonSavingsCalculator,
  [WnCComponentNames.LOCALCHANNELSLOOKUPPAGE]: LocalChannelsLookupPage,
  // [WnCComponentNames.REFERENCECOMPONENT]: ReferenceComponent,
  [WnCComponentNames.FINDRETAILER]: DtvFindRetailer,
  [WnCComponentNames.SEARCHCOMPONENT]:SearchComponent,
  [WnCComponentNames.MARKETINGBANNER]: MarketingBanner,
  [WnCComponentNames.SHOWCAROUSAL] : ShowCarousal,
  [WnCComponentNames.OFFERCARDS] : DtvOfferCards,
  [WnCComponentNames.SEARCHWATCHNOW] : SearchWatchNow,
  [WnCComponentNames.SEOCONTENT] : SeoContent,
  [WnCComponentNames.DTVCOUNTDOWN] : DtvCountdown,
  [WnCComponentNames.TVPROMISE] : TvPromise,
  [WnCComponentNames.EXITMODAL]: DtvExitModal,
  // WAT-10279: MarketingAI modal now controlled by page-load-modal orchestrator
  // [WnCComponentNames.MARKETINGAIMODAL]: MarketingAiModal,
  [WnCComponentNames.VIDEOMARQUEECAMPAIGN]: VideoMarqueeCampaign,
  [WnCComponentNames.FLAPPYPIGEON] : FlappyPigeon,
  [WnCComponentNames.SCROLLTOTOP]: ScrollToTop,
  [WnCComponentNames.RSNLOOKUPTOOL]: RsnLookupTool,
  [WnCComponentNames.RSNFEELOOKUP]: RsnFeeLookup,
  [WnCComponentNames.REVIEWCAROUSEL]: ReviewCarousel,
  [WnCComponentNames.APPDOWNLOADBANNER]: DtvAppDownloadBanner,
  [WnCComponentNames.VERTICALVIDEOCAROUSEL] : VerticalVideoCarousel,
  [WnCComponentNames.LOCALRSNTOOL]: LocalRsnTool,
  [WnCComponentNames.YOTPOPACKAGEREVIEWWIDGET]: YotpoPackageReview,
  [WnCComponentNames.PLANPORTAL]: PlanPortal,
  [WnCComponentNames.CONSPIRACYBOARD]: ConspiracyBoard,
  [WnCComponentNames.PACKAGECOMPARETOOL]: PackageCompareTool,
  [WnCComponentNames.CHANNELPACKLINEUP] : ChannelPackLineup,
  [WnCComponentNames.INPAGECHANNELPACKLINEUP] : InPageChannelPackLineup,
  [WnCComponentNames.GENREPREVIEWTOOL]: GenrePreviewTool,
  [WnCComponentNames.EQUIPMENTWIZARD]: EquipmentWizard,
  [WnCComponentNames.FEATUREDARTICLELIST]: featuredArticleList,
  [WnCComponentNames.AMBIENTVIDEOMARQUEE]: AmbientVideoMarquee,
  [WnCComponentNames.YOTPOPACKAGERATING]: YotpoPackageRating,
  [WnCComponentNames.MEDIA_CARD_IMAGE_1UP_2UP_V2]: MediaCardImage1Up2UpV2,
  // WAT-10279: Revisitor modal now controlled by page-load-modal orchestrator
  // [WnCComponentNames.REVISITORMODAL]: DtvRevisitorModal,
  [WnCComponentNames.PACKAGECAROUSEL]: PackageCarousel,
  [WnCComponentNames.FLIPCARDS]: FlipCards,
  [WnCComponentNames.GENREPACKDETAILSPANEL]: GenrePackDetailsPanel,
  [WnCComponentNames.COMPETITORCOMPARISONTABLEV2] : DtvCompetitorComparisonTable,
  [WnCComponentNames.CONTENTCARDC]: ContentCardC,
  [WnCComponentNames.CUSTOMERACQCOMPARETOOL]: CustomerAcqCompareTool,
};

const aboveTheFoldCount = 2;
/** Internal class — receives sbPreviewAllowed as a prop instead of reading context */
export class WnCMainCanvas extends React.Component<MainCanvasProps & { sbPreviewAllowed?: boolean }, MainCanvasState> {

  constructor(props: MainCanvasProps & { sbPreviewAllowed?: boolean }) {
    super(props);
    this.buildAndUpdateState();
  }

  private buildAndUpdateState = () => {
    const content = this.buildPageComponents(this.props.data) || [];
    this.state = {
      contentLow: content[1],
      contentTop: content[0],
    };
  }

  render(): JSX.Element {
    if(this.props.sbPreviewAllowed){
      this.buildAndUpdateState();
    }

    const state: MainCanvasState = this.state;

    return (
      <>
        {state.contentTop}
        {state.contentLow}
      </>
    );
  }
  private setRecommendationProp(newComponentProps) {
    return {
      ...newComponentProps,
    };
  }
  private buildPageComponents = (data: any): JSX.Element[] => {
    if (!data || !Array.isArray(data)) {
      data = [];
    }
    let returnComponents: JSX.Element[] = [];
    let nonSpacerCount = -1;
    const { props } = this;

    //TO DO : check for supress component && !suppressComponentContext[this?.props?.pagePath].includes(component?.props?.options?.id)
    // const suppressComponentContext = this.context?.globalConfigs?.SuppressComponentList;

    const builtWnCComponents: JSX.Element[] = data.map((component: any, index: number) => {
      try {
        if (component && component.type) {
          const comp = (
            component.type === "OVERWRITER"
              ? WnCComponentMap[component?.props?.component] :
            component.type === "CHANNEL_COMPARISON_TOOL" && props?.reqQueryParams?.channels == "internet"
              ? WnCComponentMap[WnCComponentNames.CHANNEL_COMPARISON_TOOL_V2] :
            component.type === "CHANNEL_LINEUP_FFP" && props?.reqQueryParams?.myFreeDtv === 'true'
              ? WnCComponentMap[WnCComponentNames.CHANNEL_LINEUP_FFP] :
              WnCComponentMap[component.type]
          );
          if (comp) {
            const WnCComponent = comp;

            // BREADCRUMBSV2 carries no image (text nav only) so it can never be the LCP,
            // but it would otherwise consume an above-the-fold slot and push the real hero
            // (e.g. DtvMarquee) out of the eager-load window -> lazy LCP. Exclude it from the count.
            // (Legacy "BREADCRUMBS" needs no exclusion: it has no WnCComponentMap entry, so the
            // `if (comp)` guard above skips it entirely and it never reaches this increment.)
            // Note: BREADCRUMBSV2 still receives aboveTheFold/lazyLoadImages for its slot below,
            // but DtvBreadCrumbs ignores those image-loading props, so the passed values are inert.
            if (component.type !== "VERTICALSPACER" && component.type !== "FLOATINGWIDGETV2" && component.type !== "BREADCRUMBSV2") {
              nonSpacerCount++;
            }

            const componentProps: any = (
              component.type === "OVERWRITER"
                ? (component?.props?.objProps || component?.props?.objProps?.[0])
                : component.props
            );

            let newComponentProps: any = {};
            if (componentProps) {
              newComponentProps = {
                ...componentProps,
                ...{
                  options: {
                    ...componentProps.options,
                    ...{ isAuthenticatedUser: props.options.isAuthenticatedUser },
                    recognizedUserData: props.options.recognizedUserData,
                    isQuizv2 :  component.type === "QUIZV2"
                  },
                  enableConfigureCart: props?.options?.enableConfigureCart
                }
              };
            }

            const spacer = {
              afterHero: {
                mobile: "100px 0px",
                medium: "120px 0px",
              },
              afterPlanCards: {
                mobile: "100px 0px",
                medium: "200px 0px",
              },
              betweenContentCards: {
                mobile: "20px 0px",
                medium: "20px 0px",
              },
            };

            const uniqueIdForVerticalSpacer =
              newComponentProps?.type === "VERTICALSPACER" ? `dtv2-VerticalSpacer${index}` :
              `dtv2-${newComponentProps?.options?.id}`;
            const uniqueDataSectionForVerticalSpacer =
              newComponentProps?.type === "VERTICALSPACER" ? `VerticalSpacer${index}` :
              `${newComponentProps?.options?.id}`;

            return (
              <section
                key={'component'+index}
                id={uniqueIdForVerticalSpacer || ('component'+index)}
                data-section={uniqueDataSectionForVerticalSpacer}
                style={{
                  ...(
                    nonSpacerCount > aboveTheFoldCount &&
                    component.type !== "SCROLLTOTOP" && component.type !== "RSNLOOKUPTOOL" && component.type !== "RSNFEELOOKUP" && component.type !== "PLANCARD" && {
                      contentVisibility: component.type === "FLOATINGWIDGETV2" ? 'inherit' : 'auto'
                  })
                }}>
                <DtvBox
                  sx={{
                    ...(!newComponentProps.spacer && {
                      marginTop:0,
                      marginBottom:0,
                      marginLeft:0,
                      marginRight:0,
                      ...(newComponentProps?.parentSpacers && {
                        marginTop: {
                          xs: newComponentProps?.parentSpacers?.mobileSpace || "20px",
                          sm: newComponentProps?.parentSpacers?.tabletSpace || "32px",
                          md: newComponentProps?.parentSpacers?.mediumSpace || "100px",
                          lg: newComponentProps?.parentSpacers?.largerSpace || "140px",
                          xl: newComponentProps?.parentSpacers?.extraLargeSpace || "180px",
                        }
                      }),
                    }),
                    ...(newComponentProps.spacer && {
                      margin: {
                        xs: newComponentProps.spacer !== "" && spacer[newComponentProps.spacer]
                          ? spacer[newComponentProps.spacer].mobile
                          : "0px",
                        md: newComponentProps.spacer !== "" && spacer[newComponentProps.spacer]
                          ? spacer[newComponentProps.spacer].medium
                          : "0px",
                      }
                    }),
                    ...(newComponentProps?.disableSticky!== undefined && !newComponentProps?.disableSticky && {
                      position: {
                        xs:"sticky",
                        md:"inherit"
                      },
                      zIndex:{
                        xs:1100,
                        md:"auto"
                      },
                      top:{
                        xs:0,
                        md:'auto'
                      },
                    }),
                  }}
                  {...(props.cms_type === "sb" && component.editableProps)}
                >
                  <WnCComponent
                    key={index}
                    aboveTheFold={
                      nonSpacerCount < aboveTheFoldCount &&
                      component.type !== "VERTICALSPACER"
                    }
                    lazyLoadImages={ !(
                      nonSpacerCount < aboveTheFoldCount &&
                      component.type !== "VERTICALSPACER"
                    )}
                    {...newComponentProps}
                    //TODO : To be removed
                    {
                      ...(props?.env !== "prod" && props?.reqQueryParams?.["pkg-orientation"] && {
                        orientation: props?.reqQueryParams?.["pkg-orientation"]
                      })
                    }
                  />
                </DtvBox>
              </section>
            );
          }
        }
      } catch (error) {
        return (
          // <div role="alert"><p style={{color: '#FFFFFF', fontWeight: '500', margin: '24px', backgroundColor: 'rgba(0,0,0,0.8)'}}>Page error: Some content not available. Please try again later.</p></div>
          <ErrorDisplay error={error} component="WncMainCanvas"/>
        );
      }
      return null;
    });

    returnComponents = [
      <React.Fragment key={`contentTop`}>{builtWnCComponents}</React.Fragment>,
    ];

    return returnComponents;
  };
}

// Generated by Copilot- Start - 2026-04-01 20:31:38
/**
 * Functional wrapper that reads sbPreviewAllowed from the optimized
 * ServerDataContext and passes it as a prop to the class component.
 * Consumers should import this wrapper instead of WnCMainCanvas directly.
 */
export default function WnCMainCanvasWrapper(props: MainCanvasProps) {
  const sbConfigs = useStoryblokConfigs();
  return <WnCMainCanvas {...props} sbPreviewAllowed={sbConfigs?.sbPreviewAllowed} />;
}
// Generated by Copilot- End - 2026-04-01 20:31:38
